import { create } from 'zustand';
import { createJSONStorage, persist, type StateStorage } from 'zustand/middleware';

import { isUiLocalePreference, type UiLocalePreference } from './locale';

export const UI_LOCALE_STORAGE_KEY = 'voll.ui.locale';

// `persist` atualiza a memória e SÓ DEPOIS grava; um `setItem` que lança (modo
// privado do Safari, quota) escaparia do `setPreference` e abortaria o
// `changeLanguage` que vem em seguida. A preferência é conveniência local: sem
// storage, o idioma vale para a sessão e ponto — o erro não sobe.
const tolerantLocalStorage: StateStorage = {
  getItem: (name) => {
    try {
      return localStorage.getItem(name);
    } catch {
      return null;
    }
  },
  setItem: (name, value) => {
    try {
      localStorage.setItem(name, value);
    } catch {
      // sem storage, sem persistência — o estado em memória já mudou
    }
  },
  removeItem: (name) => {
    try {
      localStorage.removeItem(name);
    } catch {
      // idem
    }
  },
};

interface UiLocalePreferenceState {
  preference: UiLocalePreference;
  setPreference: (preference: UiLocalePreference) => void;
}

/**
 * Preferência de idioma da INTERFACE do operador, local ao browser (não vai ao
 * accounts). Mesmo molde do `sidebar_store`, mas mora em `src/i18n` porque o
 * i18next a lê sincronamente no boot e `i18n` é folha na política de camadas
 * (não importa `data/repositories`, e repositories não importam `i18n`).
 */
export const useUiLocalePreference = create<UiLocalePreferenceState>()(
  persist(
    (set) => ({
      preference: 'auto',
      setPreference: (preference) => set({ preference }),
    }),
    {
      name: UI_LOCALE_STORAGE_KEY,
      storage: createJSONStorage(() => tolerantLocalStorage),
      partialize: (s) => ({ preference: s.preference }),
      // Valor desconhecido no storage (ex.: idioma removido) volta para `auto`.
      merge: (persisted, current) => {
        const candidate = (persisted as Partial<UiLocalePreferenceState> | undefined)?.preference;
        return { ...current, preference: isUiLocalePreference(candidate) ? candidate : 'auto' };
      },
    },
  ),
);
